import { config } from '../config';
import { round1 } from '../harness/stats';
import { formatBytes } from '../harness/storageProbe';
import { headlineFor } from '../harness/scenario';
import type { ConditionReport, HarnessReport } from '../harness/scenario';
import type { RunRecord } from '../types';

/**
 * The results surface. Design brief: legible on a phone, outdoors, in one glance.
 *
 * The order is the order of importance to the decision being made:
 *   1. ENV-1 admissibility — is this evidence at all?
 *   2. F-9 — does the design finding fire?
 *   3. per condition: headline verdict, then the four glance figures, then the detail
 *
 * Nothing here is a chart library. ENV-4 forbids third-party runtime scripts and the histogram is
 * eight divs.
 */

function pct(fraction: number): string {
  return `${(fraction * 100).toFixed(1)}%`;
}

function GateBanner({ report }: { report: HarnessReport }) {
  const ok = report.gate.admissible;
  return (
    <div className={`gate ${ok ? 'ok' : 'bad'}`}>
      {ok
        ? 'ENV-1 SATISFIED — measured on the target device as an installed PWA.'
        : 'NOT ACCEPTANCE EVIDENCE (ENV-1)'}
      {(report.gate.blockers.length > 0 || report.gate.warnings.length > 0) && (
        <ul>
          {report.gate.blockers.map((b) => (
            <li key={b}>{b}</li>
          ))}
          {report.gate.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Figure({
  value,
  label,
  budget,
}: {
  value: number;
  label: string;
  budget?: number;
}) {
  const state = budget === undefined ? '' : value <= budget ? 'under' : 'over';
  return (
    <div className={`figure ${state}`}>
      <div className="value">{Math.round(value)}</div>
      <div className="label">{label}</div>
    </div>
  );
}

function Histogram({ report }: { report: ConditionReport }) {
  const max = Math.max(1, ...report.commitHistogram.map((b) => b.count));
  return (
    <div className="hist">
      {report.commitHistogram.map((b) => {
        const upper = Number.isFinite(b.toMs) ? `${b.toMs}` : '∞';
        const over = b.fromMs >= config.ACK_COMMIT_BUDGET_MS;
        return (
          <div className={`hist-row ${over ? 'over' : ''}`} key={`${b.fromMs}-${upper}`}>
            <span>
              {b.fromMs}–{upper} ms
            </span>
            <span className="hist-bar" style={{ width: `${(b.count / max) * 100}%` }} />
            <span>{b.count}</span>
          </div>
        );
      })}
    </div>
  );
}

function ConditionCard({ report, run }: { report: ConditionReport; run: RunRecord | undefined }) {
  const primary = report.checks[0];
  const verdictClass = primary?.verdict ?? 'inconclusive';

  return (
    <div className="card">
      <h2>
        {report.condition} · {report.mode === 'liveTap' ? 'real taps' : 'synthetic'} · n=
        {report.sampleCount}
      </h2>
      <div className={`headline ${verdictClass}`}>{headlineFor(report)}</div>

      <div className="figures">
        <Figure value={report.commit.p50} label="commit p50" budget={config.ACK_COMMIT_BUDGET_MS} />
        <Figure value={report.commit.p95} label="commit p95" budget={config.ACK_COMMIT_BUDGET_MS} />
        <Figure value={report.commit.p99} label="commit p99" budget={config.ACK_COMMIT_BUDGET_MS} />
        <Figure value={report.commit.max} label="commit max" budget={config.ACK_COMMIT_BUDGET_MS} />
      </div>
      <div className="figures">
        <Figure value={report.total.p50} label="ack p50" budget={config.TAP_ACK_BUDGET_MS} />
        <Figure value={report.total.p95} label="ack p95" budget={config.TAP_ACK_BUDGET_MS} />
        <Figure value={report.total.p99} label="ack p99" budget={config.TAP_ACK_BUDGET_MS} />
        <Figure value={report.total.max} label="ack max" budget={config.TAP_ACK_BUDGET_MS} />
      </div>

      {report.mode === 'liveTap' ? (
        <p className="note">
          Real taps. The ack figures above include {round1(report.input.p50)} ms (p50) of touch
          delivery before any of our code runs — the leg the automatic runs cannot see.
        </p>
      ) : (
        <p className="note">
          Synthetic taps: dispatched from inside JavaScript, so the ack figures above{' '}
          <strong>exclude touch delivery entirely</strong> and understate the real path.
        </p>
      )}

      {report.provenance === 'unknown' && (
        <div className="error">
          PROVENANCE UNKNOWN — this run was recorded before the harness checked whether another
          run’s heat load overlapped its sampling window, so it cannot attest that it was measured
          clean. It is kept, but it is <strong>excluded from the aggregate verdicts</strong> rather
          than allowed to vote on them. Not checked is not the same as checked and fine.
        </div>
      )}

      {report.foreignHeatWorkersDuringRun !== null && report.foreignHeatWorkersDuringRun > 0 && (
        <div className="error">
          CONTAMINATED — {report.foreignHeatWorkersDuringRun} heat worker(s) belonging to another
          run were live while this one sampled. It measured under load it did not ask for, so these
          figures describe a device state no user will ever be in. Discard this run.
        </div>
      )}

      {!report.gate.admissible && report.gate.blockers.length > 0 && (
        <ul className="note" style={{ marginTop: 8, paddingLeft: 18 }}>
          {report.gate.blockers.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      )}

      <table className="checks">
        <tbody>
          {report.checks.map((c) => (
            <tr key={c.id}>
              <td>
                <span className={`badge ${c.verdict}`}>{c.verdict}</span>
              </td>
              <td>
                <strong>{c.id}</strong> — {c.label}
                <br />
                <span className="note">
                  {c.statistic}: {round1(c.observedMs)} ms vs {c.budgetMs} ms
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="note" style={{ marginTop: 10 }}>
        Over {config.ACK_COMMIT_BUDGET_MS} ms commit: {report.overCommitBudget.count} (
        {pct(report.overCommitBudget.fraction)}) · over {config.TAP_ACK_BUDGET_MS} ms ack:{' '}
        {report.overNfr1Budget.count} ({pct(report.overNfr1Budget.fraction)})
        {report.failureCount > 0 && ` · ${report.failureCount} trial(s) FAILED`}
        {report.p99Underpowered && ' · p99 underpowered at this sample size'}
      </p>

      <Histogram report={report} />

      {run && (
        <dl className="kv" style={{ marginTop: 12 }}>
          <dt>seeded rows</dt>
          <dd>
            {run.seed.observationRows.toLocaleString()} observations ·{' '}
            {run.seed.hypothesisRows.toLocaleString()} hypotheses
            {run.seed.bulkBlobBytes > 0 && ` · ${formatBytes(run.seed.bulkBlobBytes)} audio bulk`}
          </dd>
          {run.condition === 'thermal' && (
            <>
              <dt>thermal load</dt>
              <dd>
                {Math.round(run.soakCompletedS)} s soak · {run.heatWorkers} workers. No thermal API
                exists on iOS, so this is “after sustained multi-core load”, not a temperature.
              </dd>
            </>
          )}
          <dt>logger flushes inside window</dt>
          <dd>
            {run.logFlushesDuringRun}
            {run.logFlushesDuringRun === 0
              ? ' — clean (design §12.1)'
              : ' — CONTAMINATED, do not use for A-1'}
          </dd>
          {run.notes && (
            <>
              <dt>note</dt>
              <dd>{run.notes}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

/**
 * The two things that must be visible without scrolling, so they are rendered above the controls:
 * whether this is evidence at all (ENV-1), and whether F-9 fires. Everything else supports them.
 */
export function ReportHeader({ report }: { report: HarnessReport }) {
  const basis = report.nfr1Basis;
  const a1Class =
    report.a1.outcome === 'passing' ? 'holds' : report.a1.outcome === 'failing' ? 'breached' : '';
  return (
    <>
      <GateBanner report={report} />

      {/* The question this build unit exists to answer, first and in plain words. A reader used to
          meet "F-9 FIRES" first and had to reconstruct the truth from per-run tables — which is the
          failure mode the instrument exists to prevent. */}
      <div className={`f9 ${a1Class}`}>
        <div className="t">{report.a1.headline}</div>
        <div className="d">{report.a1.detail}</div>
      </div>

      <div className={`f9 ${report.f9.outcome}`}>
        <div className="t">{report.f9.headline}</div>
        <div className="d">{report.f9.detail}</div>
      </div>
      {report.excluded.length > 0 && (
        <div className="card">
          <h2>Excluded from the verdicts</h2>
          <p className="sub">
            Kept, not deleted — deleting evidence is worse than mis-scoring it. These runs are on
            the device and in the export; they simply do not vote.
          </p>
          <dl className="kv">
            {report.excluded.map((x) => (
              <div key={x.runId} style={{ display: 'contents' }}>
                <dt>
                  {x.condition} · {x.runId}
                </dt>
                <dd>{x.reason}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Which measurements the NFR-1 claim rests on. Without this, a synthetic 17 ms and a real
          41 ms sit side by side on the same screen looking like the same kind of number. */}
      <div className={`f9 ${basis.basis === 'liveTap' ? 'holds' : ''}`}>
        <div className="t">
          NFR-1 basis:{' '}
          {basis.basis === 'liveTap'
            ? 'real taps'
            : basis.basis === 'synthetic'
              ? 'synthetic taps only — understated'
              : 'none yet'}
        </div>
        <div className="d">{basis.note}</div>
      </div>
    </>
  );
}

export function ResultsView({
  report,
  runs,
}: {
  report: HarnessReport;
  runs: readonly RunRecord[];
}) {
  const latest = runs[0];

  return (
    <>
      {report.conditions.map((c) => (
        <ConditionCard key={c.runId} report={c} run={runs.find((r) => r.id === c.runId)} />
      ))}

      {latest && (
        <div className="card">
          <h2>BAK-3 — storage</h2>
          <dl className="kv">
            <dt>usage</dt>
            <dd>{formatBytes(latest.storage.usageBytes)}</dd>
            <dt>quota</dt>
            <dd>{formatBytes(latest.storage.quotaBytes)}</dd>
            <dt>used</dt>
            <dd>
              {latest.storage.usageFraction === null
                ? '—'
                : pct(latest.storage.usageFraction)}
              {latest.storage.usageFraction !== null &&
                latest.storage.usageFraction > config.STORAGE_WARN_FRACTION &&
                ' — over the warn threshold'}
            </dd>
            <dt>persisted before</dt>
            <dd>{String(latest.storage.persistedBefore)}</dd>
            <dt>persist() requested</dt>
            <dd>{String(latest.storage.persistRequested)}</dd>
            <dt>persisted after</dt>
            <dd>
              {String(latest.storage.persistedAfter)}
              {latest.storage.persistedAfter === false &&
                ' — assumption A-4 does not hold on this device; eviction risk is higher than the design assumes.'}
            </dd>
            {latest.storage.error && (
              <>
                <dt>error</dt>
                <dd>{latest.storage.error}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {latest && (
        <div className="card">
          <h2>Device</h2>
          <dl className="kv">
            <dt>user agent</dt>
            <dd>{latest.environment.userAgent}</dd>
            <dt>installed PWA</dt>
            <dd>{String(latest.environment.standalone)}</dd>
            <dt>screen</dt>
            <dd>
              {latest.environment.screenW}×{latest.environment.screenH} @
              {latest.environment.devicePixelRatio}x
            </dd>
            <dt>cores</dt>
            <dd>{latest.environment.hardwareConcurrency ?? '—'}</dd>
            <dt>timer resolution</dt>
            <dd>
              {latest.environment.timerResolutionMs.toFixed(3)} ms — every figure above is quantised
              to this step
            </dd>
          </dl>
        </div>
      )}
    </>
  );
}
